import { createId as cuid } from "@paralleldrive/cuid2"
import { createFileRoute, useLoaderData, useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import useObjectURL from "use-object-url"
import { z } from "zod"

import { MarkType } from "@/components/icons/MarkIcon.utils"
import { SpinnerLayout } from "@/components/layouts/SpinnerLayout"
import { useGeolocationRef } from "@/helpers/GeolocationProvider"
import { useImagePickerWithRotation } from "@/helpers/hooks/useImagePicker"
import { useUpsertPhotoHook } from "@/helpers/hooks/useStore"
import { LINKS } from "@/routing/links"
import { resolveClaimCoveredStep } from "@/sections/claim/resolveClaimCoveredStep"
import { useClaimPhotoModel } from "@/sections/claim/useClaimPhotoModel"
import { GuideForceStep } from "@/sections/guide/GuideForceStep"
import { GuideMakePhoto } from "@/sections/guide/GuideMakePhoto"
import { GuidePhotoUploader } from "@/sections/guide/GuidePhotoUploader"
import {
  addClaimPhotoCopy,
  deleteClaimPhotoCopies,
  findOrderedSteps,
  findPhotoById,
  softDeletePhoto,
  useSessionDetail,
} from "@/services/dexie"

export const Route = createFileRoute("/$sessionId/_session/claim/new")({
  component: ClaimNewPage,
  // replacesPhotoId is set when retaking an existing claim photo — the old photo is deleted on save
  validateSearch: z.object({ replacesPhotoId: z.string().optional() }),
})

function ClaimNewPage() {
  const { t } = useTranslation()
  const { sessionId } = Route.useParams()
  const { replacesPhotoId } = Route.useSearch()
  const { enableUpload, viewsCheck, qualityCheck } = useLoaderData({ from: "/$sessionId/_session/claim" })
  const navigate = useNavigate()

  const { data: sessionDetail, isLoading: isLoadingDetail } = useSessionDetail(sessionId)

  const [loading, setLoading] = useState(false)
  const [forced, setForced] = useState(false)

  const { file, files, select } = useImagePickerWithRotation({ enableUpload })
  const fileUrl = useObjectURL(file ?? null)
  const { getCurrentGeolocation } = useGeolocationRef()
  const upsertPhoto = useUpsertPhotoHook()

  const modelEnabled = viewsCheck && qualityCheck
  const { isModelEnabled, processClaimPhoto, claimWarningProps, retryCount, prediction } = useClaimPhotoModel({
    enabled: modelEnabled,
    setLoading,
  })

  const photoIdRef = useRef<string>(undefined)
  const capturedFileRef = useRef<File>(undefined)

  // New capture — assign a fresh photo id, reset force, run the model
  useEffect(() => {
    if (!file || file === capturedFileRef.current) return
    capturedFileRef.current = file
    photoIdRef.current = cuid()
    setForced(false)
    processClaimPhoto(file)
  }, [file, processClaimPhoto])

  const saveAndNavigate = useCallback(async () => {
    const coords = getCurrentGeolocation()
    const location = coords
      ? { latitude: coords.latitude, longitude: coords.longitude, accuracy: coords.accuracy ?? -1 }
      : null
    const photoId = photoIdRef.current ?? cuid()

    await upsertPhoto({
      sessionId,
      photoId,
      type: "Claim",
      files,
      location,
      retries: retryCount,
      canUploadMultiple: true,
      carViewBrowserModelResult: prediction,
    })

    // Retake: delete the photo being replaced (delete-sync removes it from the server too).
    // The retake action is only offered before a marker exists, so no marker/detail cascade is needed.
    if (replacesPhotoId && replacesPhotoId !== photoId) {
      const toBeReplaced = await findPhotoById(replacesPhotoId)
      if (toBeReplaced?.type === "Claim" && toBeReplaced.sessionId === sessionId) {
        await softDeletePhoto(replacesPhotoId)
        await deleteClaimPhotoCopies({ sourcePhotoId: replacesPhotoId, sessionId })
      }
    }

    // Claim photo already shows a view required later, so reuse it and skip that step.
    // Runs after the retake cleanup so a step freed by the replaced photo can be covered again.
    const coveredStep = sessionDetail
      ? resolveClaimCoveredStep({
          enabled: sessionDetail.reuseClaimPhotoForViewsEnabled,
          prediction,
          hasWarning: !!claimWarningProps,
          steps: await findOrderedSteps(sessionId),
          photoQualityValidations: sessionDetail.photoQualityValidations,
        })
      : undefined
    if (coveredStep) {
      await addClaimPhotoCopy({ sourcePhotoId: photoId, type: coveredStep.name })
    }

    await navigate({ to: LINKS.claimNewMarker(sessionId, photoId) })
  }, [
    getCurrentGeolocation,
    upsertPhoto,
    sessionId,
    files,
    retryCount,
    prediction,
    sessionDetail,
    claimWarningProps,
    navigate,
    replacesPhotoId,
  ])

  // Accepted: model disabled (no validation) OR a prediction with no quality warning (or forced)
  const isAccepted = !!file && (!isModelEnabled || (!!prediction && (!claimWarningProps || forced)))

  // Force is only allowed after the configured number of attempts, same as the view flow
  const retryCountBeforeForceAllowed =
    (sessionDetail?.requiredAttemptsBeforePhotoForce ?? 0) < 0
      ? Infinity
      : (sessionDetail?.requiredAttemptsBeforePhotoForce ?? 0)

  const handleForceConfirm = useMemo(
    () => (retryCount >= retryCountBeforeForceAllowed ? () => setForced(true) : undefined),
    [retryCount, retryCountBeforeForceAllowed],
  )

  if (isLoadingDetail || loading) {
    return <SpinnerLayout />
  }

  // Failed a quality/view check — error screen with retake / force, same as the view flow
  if (file && prediction && claimWarningProps && !forced) {
    return (
      <GuideForceStep
        fileUrl={fileUrl ?? ""}
        expectedTypeName={t("claim.capture.subject")}
        title={claimWarningProps.title}
        text={claimWarningProps.text}
        mark={MarkType.Cross}
        stepType="Photo"
        onRetry={select}
        onForceConfirm={handleForceConfirm}
      />
    )
  }

  // Accepted — upload and continue to marker drawing (mount-effect uploader, same as the view flow)
  if (isAccepted) {
    return <GuidePhotoUploader upload={saveAndNavigate} />
  }

  // No photo yet, model still loading, or a prediction error — show the capture screen (retake)
  return (
    <GuideMakePhoto
      type="Claim"
      heading={t("claim.capture.title")}
      text={t("claim.capture.text")}
      onMakePhoto={select}
      enableUpload={enableUpload}
    />
  )
}
